import {
  Heart,
  MessageCircle,
  Share2,
  Upload,
  Volume2,
  Play,
  Flame,
} from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: Reels - Whistll's share surface.
 *
 * Full-screen vertical feed of game clips, same shape as Instagram
 * Reels / TikTok. Swipe up for next. Players upload clips via the
 * Match Room within 24 hours of the game ending; once live on Reels,
 * clips stay up (they don't expire). Mirrors
 * apps/legacy-mobile/src/pages/Reels.jsx - volt action rail on the
 * right, uploader + caption overlaid bottom-left, and a four-tab
 * bottom nav (Home / Share / Bookings / Profile) with Share active.
 */
export function ReelsMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Full-bleed "video" fills the phone screen */}
      <div className="relative h-full w-full overflow-hidden">
        {/* Fake video - court scene gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 120% 80% at 50% 35%, rgba(255,140,66,0.25) 0%, rgba(255,98,0,0.12) 25%, #0d1117 60%)",
          }}
        />
        {/* Court floor lines for vibe */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-1/2 opacity-40"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,98,0,0.08) 40%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute left-1/2 top-[38%] h-16 w-16 -translate-x-1/2 rounded-full border border-[#FF6200]/30"
        />
        <div
          aria-hidden
          className="absolute left-1/2 top-[58%] h-[1px] w-40 -translate-x-1/2 bg-[#FF6200]/25"
        />

        {/* Top gradient overlay for status bar readability */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 to-transparent"
        />

        {/* Bottom gradient overlay for caption readability */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black via-black/50 to-transparent"
        />

        {/* Status bar */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold text-[#F5F5DC]">
          <span>9:41</span>
          <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
        </div>

        {/* Top tabs - Following / For You */}
        <div className="relative z-10 mt-1 flex items-center justify-center gap-5">
          <span className="text-[11px] font-semibold text-[#F5F5DC]/60">Following</span>
          <span className="relative text-[12px] font-black text-[#F5F5DC]">
            For You
            <span className="absolute -bottom-1 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-[#FF6200]" />
          </span>
        </div>

        {/* Central play overlay (indicates paused) */}
        <div className="absolute left-1/2 top-[42%] z-10 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
          <Play
            className="h-5 w-5 text-white"
            fill="white"
            strokeWidth={0}
          />
        </div>

        {/* POTM flame badge */}
        <div className="absolute left-4 top-16 z-10 inline-flex items-center gap-1 rounded-full bg-[#FF6200] px-2 py-0.5 text-[9px] font-black text-[#0d1117]">
          <Flame className="h-2.5 w-2.5" />
          POTM CLIP
        </div>

        {/* Right side action rail */}
        <div className="absolute bottom-28 right-2.5 z-10 flex flex-col items-center gap-4">
          <ActionButton icon={<Heart className="h-5 w-5" fill="#FF6200" />} count="2.4k" color="#FF6200" />
          <ActionButton icon={<MessageCircle className="h-5 w-5 text-white" />} count="318" />
          <ActionButton icon={<Share2 className="h-5 w-5 text-white" />} count="92" />
          <ActionButton icon={<Volume2 className="h-5 w-5 text-white" />} />
          <ActionButton icon={<Upload className="h-5 w-5 text-white" />} label="Post" />
        </div>

        {/* Bottom-left uploader + caption */}
        <div className="absolute inset-x-3 bottom-14 z-10">
          <div className="mb-1.5 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#FF6200] to-[#FF8C42] text-[10px] font-black text-[#0d1117]">
              DK
            </div>
            <span className="text-[11px] font-black text-[#F5F5DC]">
              @darren_k
            </span>
            <button className="rounded-full border border-[#FF6200] bg-transparent px-2 py-0.5 text-[9px] font-black text-[#FF6200]">
              Follow
            </button>
          </div>
          <p className="mb-1 max-w-[200px] text-[10px] font-semibold leading-snug text-[#F5F5DC]">
            Crossover + and-one 🔥 Brixton Kings 42-38 vs Camden Heat
          </p>
          <p className="flex items-center gap-1 text-[9px] text-[#F5F5DC]/70">
            <span>#5v5</span>
            <span>·</span>
            <span>#brixton</span>
            <span>·</span>
            <span>#pickup</span>
          </p>
        </div>

        {/* Bottom nav - four tabs total, Share (Reels) active */}
        <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-around border-t border-white/5 bg-black/70 px-6 py-2 backdrop-blur-md">
          <BottomTab label="Home" />
          <BottomTab label="Share" active />
          <BottomTab label="Bookings" />
          <BottomTab label="Profile" />
        </div>
      </div>
    </PhoneMock>
  );
}

function ActionButton({
  icon,
  count,
  label,
  color = "#F5F5DC",
}: {
  icon: React.ReactNode;
  count?: string;
  label?: string;
  color?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      {icon}
      {count && (
        <span className="text-[9px] font-black" style={{ color }}>
          {count}
        </span>
      )}
      {label && (
        <span className="text-[9px] font-bold text-white">{label}</span>
      )}
    </div>
  );
}

function BottomTab({ label, active = false }: { label: string; active?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span
        className="h-1 w-1 rounded-full"
        style={{ background: active ? "#FF6200" : "transparent" }}
      />
      <span
        className="text-[8px] font-bold"
        style={{ color: active ? "#FF6200" : "#F5F5DC" }}
      >
        {label}
      </span>
    </div>
  );
}
